import { Box, Container, MantineProvider } from '@mantine/core'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'

import Footer from '@/containers/navigation/Footer'
import Header from '@/containers/navigation/Header'
import { useFetch } from '@/lib/hooks'
import { SiteData as SiteDataType } from '@/types'

import { BreadcrumbNav } from './components'

interface Props {
  initialData?: SiteDataType
  children?: React.ReactNode
}

export default function SupportLayout({ initialData, children }: Props) {
  // Hooks
  const router = useRouter()
  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  // States
  const [headerHeight, setHeaderHeight] = useState(0)
  const [footerHeight, setFooterHeight] = useState(0)

  // Constants
  const { site: siteSlug } = router.query || {}

  // Fetch
  const { data: siteData } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site
  })

  // Custom theme
  const customColor = siteData?.theme?.configuration?.customColor
  const customTheme: object | undefined =
    Array.isArray(customColor) && customColor.length === 10
      ? { colors: { custom: customColor }, primaryColor: 'custom' }
      : undefined

  // Effects
  useEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight || 0)
    setFooterHeight(footerRef?.current?.clientHeight || 0)
  }, [])

  return (
    <MantineProvider theme={customTheme} inherit>
      <Box
        sx={{ position: 'sticky', top: 0, left: 0, width: '100%', height: 'auto', zIndex: 101 }}
        ref={headerRef}>
        <Header siteData={siteData} />
        <BreadcrumbNav siteData={siteData} />
      </Box>
      <Container
        fluid
        p={0}
        m={0}
        sx={{
          position: 'relative',
          margin: 0,
          padding: 0,
          minHeight: `calc(100vh - ${headerHeight}px - ${footerHeight}px)`
        }}>
        {children}
      </Container>
      <Footer siteData={siteData} footerRef={footerRef} />
    </MantineProvider>
  )
}
